import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import Toast from '../components/ui/Toast';
import { errorService, ErrorType } from '../services/core/errorService';

interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

interface ToastContextType {
  showToast: (message: string, type?: 'success' | 'error' | 'info' | 'warning', duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showConfirm: (message: string, options?: { confirmText?: string; cancelText?: string }) => Promise<boolean>;
  handleErrorFromService: (error: unknown) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', duration?: number) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: ToastItem = {
      id,
      message,
      type,
      duration
    };

    setToasts(prev => [...prev, newToast]);
  };

  const showSuccess = (message: string, duration?: number) => showToast(message, 'success', duration);
  const showError = (message: string, duration?: number) => showToast(message, 'error', duration);
  const showInfo = (message: string, duration?: number) => showToast(message, 'info', duration);
  const showWarning = (message: string, duration?: number) => showToast(message, 'warning', duration);

  // Confirmation dialog using browser's confirm
  const showConfirm = (message: string, options?: { confirmText?: string; cancelText?: string }) => {
    return Promise.resolve(window.confirm(message));
  };

  // Handle errors from error service
  const handleErrorFromService = (error: unknown) => {
    const classification = errorService.handleError(error);
    showError(classification.userMessage);
  };

  // Set up error service listeners
  useEffect(() => {
    // Subscribe to authentication errors
    const unsubscribeAuth = errorService.onError(ErrorType.AUTHENTICATION, (error) => {
      showError(error.userMessage, 5000);
    });

    // Subscribe to network errors
    const unsubscribeNetwork = errorService.onError(ErrorType.NETWORK, (error) => {
      showError(error.userMessage, 5000);
    });

    // Subscribe to server errors
    const unsubscribeServer = errorService.onError(ErrorType.SERVER_ERROR, (error) => {
      showError(error.userMessage, 5000);
    });

    // Subscribe to rate limit errors
    const unsubscribeRateLimit = errorService.onError(ErrorType.RATE_LIMIT, (error) => {
      showWarning(error.userMessage, 5000);
    });

    return () => {
      unsubscribeAuth();
      unsubscribeNetwork();
      unsubscribeServer();
      unsubscribeRateLimit();
    };
  }, []);

  return (
    <ToastContext.Provider value={{
      showToast,
      showSuccess,
      showError,
      showInfo,
      showWarning,
      showConfirm,
      handleErrorFromService
    }}>
      {children}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </ToastContext.Provider>
  );
};